import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { getSchoolUrl } from "@/lib/slug";
import { Sparkles, CheckCircle2, Clock } from "lucide-react";

export default function ReviewSuccess() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const schoolName = sp.get("school_name") || "sekolah pilihan Anda";
  const schoolId = sp.get("school_id");
  // Ambil hanya nama depan pengguna (kata pertama). Fallback ke sapaan ramah bila kosong.
  const rawName = (user?.name || "").trim();
  const firstName = rawName ? rawName.split(/\s+/)[0] : "Teman";

  useEffect(() => {
    // Small confetti-ish CSS burst is triggered via keyframes below
    document.title = "Terima kasih — The School Reports";
  }, []);

  const waText = encodeURIComponent(
    `Halo! Saya baru saja memberikan review tentang sekolah anak saya di The School Reports. ` +
    `Yuk, ikut berbagi pengalaman sekolah anak Anda juga di sini: ${window.location.origin}`
  );
  const waHref = `https://wa.me/?text=${waText}`;

  const threadsText = encodeURIComponent(
    `Saya baru saja menulis review tentang sekolah di The School Reports! ` +
    `Baca dan bagikan pengalamanmu juga di sini 👉 ${window.location.origin}`
  );
  const threadsHref = `https://threads.net/intent/post?text=${threadsText}`;

  return (
    <div data-testid="review-success-page" className="min-h-[80vh] max-w-3xl mx-auto px-6 lg:px-10 py-16 flex items-center">
      <div className="w-full">
        {/* Celebration burst */}
        <div className="relative w-24 h-24 mx-auto">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#22C55E] via-[#16A34A] to-[#166534] opacity-90 animate-ping-slow" />
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#22C55E] via-[#16A34A] to-[#166534] flex items-center justify-center shadow-2xl">
            <CheckCircle2 size={44} className="text-white" strokeWidth={2.5} />
          </div>
          <Sparkles className="absolute -top-2 -right-2 text-[#FED7AA]" size={20} />
          <Sparkles className="absolute -bottom-2 -left-2 text-[#FDE047]" size={18} />
        </div>

        <h1 data-testid="success-headline" className="mt-8 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#1F2937] text-center leading-tight">
          Terima kasih, <span className="text-[#1E3A8A]">{firstName}</span>!
        </h1>
        <p className="mt-4 text-[#4B5563] text-center text-base sm:text-lg leading-relaxed">
          Review Anda tentang <strong>{schoolName}</strong> sudah <strong>tayang</strong> dan akan membantu ribuan orang tua lain dalam memilih masa depan anak.
        </p>

        <div className="mt-8 mx-auto max-w-lg bg-[#EFF6FF] border border-[#DBEAFE] rounded-2xl p-5 flex items-start gap-3" data-testid="moderation-notice">
          <Clock className="text-[#1E3A8A] shrink-0 mt-0.5" size={18} />
          <div>
            <div className="text-[#1E3A8A] font-semibold text-sm">Sedang diverifikasi tim</div>
            <div className="text-sm text-[#1F2937] mt-1 leading-relaxed">
              Review Anda langsung tayang, dan tim editorial sedang memverifikasi keasliannya. Indikator kecil "Sedang diverifikasi tim" akan tampil pada review Anda (hanya terlihat oleh Anda) sampai proses selesai.
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="success-share-whatsapp"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#1EBE58] text-white font-medium transition-colors whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M19.11 17.28c-.28-.14-1.65-.81-1.9-.9-.26-.09-.44-.14-.63.14-.18.28-.72.9-.88 1.09-.16.18-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.37-.83-.74-1.38-1.65-1.55-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49H10c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34s1 2.72 1.14 2.9c.14.19 1.97 3.01 4.78 4.22.67.29 1.19.46 1.6.59.67.21 1.28.18 1.76.11.54-.08 1.65-.68 1.89-1.34.23-.65.23-1.21.16-1.34-.07-.13-.26-.21-.54-.35zM16 3.2C8.94 3.2 3.2 8.94 3.2 16c0 2.26.6 4.47 1.74 6.42L3 29l6.75-1.86A12.7 12.7 0 0 0 16 28.8C23.06 28.8 28.8 23.06 28.8 16S23.06 3.2 16 3.2z" />
            </svg>
            Bagikan ke Grup WhatsApp Wali Murid
          </a>
          <a
            href={threadsHref}
            target="_blank"
            rel="noreferrer"
            data-testid="success-share-threads"
            className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#101010] hover:bg-[#1F2937] text-white font-medium transition-colors whitespace-nowrap"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 192" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.893 8.111 5.869 14.05-7.351-1.249-15.302-1.633-23.797-1.144-23.923 1.378-39.303 15.332-38.27 34.72.525 9.836 5.42 18.297 13.783 23.822 7.072 4.671 16.185 6.949 25.653 6.437 12.51-.687 22.322-5.459 29.164-14.185 5.198-6.63 8.482-15.222 9.926-26.048 5.925 3.575 10.32 8.28 12.744 13.937 4.126 9.612 4.352 25.4-8.508 38.245-11.443 11.427-25.24 16.377-46.147 16.535-23.194-.172-40.744-7.617-52.16-22.128-10.694-13.586-16.219-33.222-16.428-58.377.209-25.155 5.734-44.791 16.428-58.377 11.416-14.511 28.966-21.956 52.16-22.128 23.362.174 41.222 7.652 53.098 22.227 5.822 7.144 10.212 16.13 13.11 26.615l16.135-4.335c-3.51-12.907-9.03-24.028-16.541-33.244C160.788 15.833 137.898 6.51 108.005 6.4v.001h-.093c-29.833.207-52.462 9.51-67.267 27.65C27.474 50.081 20.611 72.4 20.4 100.401v.037c.211 27.994 7.075 50.315 20.409 66.336 14.802 18.14 37.397 27.451 67.209 27.663l.055-.011c26.502-.183 45.185-7.121 60.579-22.499 20.14-20.123 19.531-45.336 12.891-60.848-4.759-11.13-13.83-20.174-26.005-26.09Zm-43.052 32.007c-10.48.59-21.375-4.115-21.912-14.192-.398-7.47 5.317-15.809 22.552-16.802 1.973-.114 3.91-.169 5.813-.169 6.26 0 12.117.61 17.44 1.774-1.986 24.803-13.641 28.821-23.893 29.389Z" />
            </svg>
            Bagikan ke Instagram Threads
          </a>
          {schoolId && (
            <Link to={getSchoolUrl({ school_id: schoolId, name: schoolName })} data-testid="success-back-to-school">
              <Button variant="outline" className="rounded-full h-12 px-6 border-[#EAE7E0] text-[#1F2937]">
                Kembali ke halaman sekolah
              </Button>
            </Link>
          )}
        </div>

        <div className="mt-8 text-center text-xs text-[#4B5563]">
          Ingin membantu lebih jauh? <Link to="/sekolah" className="text-[#1E3A8A] hover:text-[#E07A5F]">Beri review sekolah lain →</Link>
        </div>
      </div>

      <style>{`
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.9; }
          80%, 100% { transform: scale(1.6); opacity: 0; }
        }
        .animate-ping-slow { animation: ping-slow 1.6s cubic-bezier(0, 0, 0.2, 1) infinite; }
      `}</style>
    </div>
  );
}
